import { NewOrderType, TNewOrderForm } from "@/entities/new-order";
import { useAppSelector } from "@/Hooks/redux/useAppSelector";
import CreateNewOrderModal from "@/Pages/CreateCampaignPage/Components/CreateNewOrderModal";
import { INewCampaignObject } from "@/Pages/CreateCampaignPage/Models/CountryModel";
import { getAppStore } from "@/shared/helpers";
import { useCreateNewCampaignMutation } from "@/Store/APIs/Campaign.api";
import { useLazyGetDataQuery } from "@/Store/APIs/ClientData.api";
import modalStyles from "@/Theme/modalsElements.module.scss";
import BasicButton from "@/UI/BasicButton";
import { throwNewToastError, throwNewToastSuccess } from "@/Utils/MainUtils";
import { Flex } from "@mantine/core";
import { Modal } from "@mui/material";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  buildImpressionsInfo,
  cleanCountryKeywords,
  formatDelayedStart,
  validateOrder,
} from "../../utils";

type TNewOrderTotalBlockProps = {
  onReset: () => void;
};

export const NewOrderTotalBlock = ({ onReset }: TNewOrderTotalBlockProps) => {
  const { handleSubmit } = useFormContext<TNewOrderForm>();
  const navigate = useNavigate();

  const { user } = useAppSelector((state) => state.clientDataSlice);
  const [createNewCampaign, { isLoading }] = useCreateNewCampaignMutation();
  const [fetchNewUserData] = useLazyGetDataQuery();

  const [openSubmitModal, setOpenSubmitModal] = useState(false);
  const [finalOrderState, setFinalOrderState] =
    useState<INewCampaignObject | null>(null);

  const onSubmit = (data: TNewOrderForm) => {
    // Подготовка данных заказа
    const cleanKeywords = cleanCountryKeywords(
      data.countriesList,
      getAppStore(data.app?.url || "apple"),
    );
    const formatedDelayedStart = formatDelayedStart(data.delayedStart.value);

    const orderObject: INewCampaignObject = {
      type: data.type,
      app_id: data.app?.id ?? 0,
      date_start: data.dateStart,
      date_stop: data.dateStop,
      delayed_start: formatedDelayedStart,
      settings: data.attributesList,
      keywords: cleanKeywords,
    };

    // Добавление информации об impressions для типа install_with_views
    if (data.type === NewOrderType.install_with_views) {
      const impressionsInfo = buildImpressionsInfo(data.countriesList);
      orderObject["info"] = {
        impressions_additional_info: impressionsInfo,
      };
    }

    // Валидация заказа
    const isValid = validateOrder({
      orderObject,
      delayedStartEnabled: data.delayedStart.enable,
      delayedStartValue: data.delayedStart.value,
      app: data.app,
      user,
      countriesList: data.countriesList,
      attributesList: data.attributesList,
    });

    if (!isValid) {
      return;
    }

    // Открытие модала подтверждения
    setFinalOrderState(orderObject);
    setOpenSubmitModal(true);
  };

  const handleSubmitCreateNewOrder = () => {
    if (finalOrderState) {
      createNewCampaign(finalOrderState)
        .unwrap()
        .then((result) => {
          if (result.status === "ok") {
            onReset();

            setOpenSubmitModal(false);
            navigate("/orders");
            throwNewToastSuccess("Campaign created successfully");

            return fetchNewUserData();
          }
        })
        .catch((error) => {
          setOpenSubmitModal(false);

          if (
            "data" in error &&
            error.data &&
            "error" in error.data &&
            error.data.error
          ) {
            throwNewToastError(error.data.error);
          }

          if (
            "data" in error &&
            error.data &&
            "status" in error.data &&
            error.data.status
          ) {
            throwNewToastError(error.data.status);
          }

          console.error(error);
        });
    }
  };

  return (
    <Flex w={380} ml="auto" gap="lg" align="center">
      <BasicButton
        onClick={handleSubmit(onSubmit)}
        size="md"
        text="Start promotion"
        variant="blue"
      />
      <BasicButton
        onClick={onReset ?? (() => {})}
        size="md"
        text="Clear"
        variant="red"
        title="Clear all campaign settings"
      />

      <Modal
        open={openSubmitModal}
        onClose={() => setOpenSubmitModal(false)}
        className={modalStyles.modal}
      >
        <CreateNewOrderModal
          handleSubmitCreateNewOrder={handleSubmitCreateNewOrder}
          onClose={() => setOpenSubmitModal(false)}
          isLoading={isLoading}
        />
      </Modal>
    </Flex>
  );
};
